import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Modal, Select, Input, DatePicker,
    Row, Col, message, Tag, Statistic, Tooltip
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    ReloadOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import { usePeriods } from '../../hooks/usePeriods';
import moment from 'moment';
import {
    trailCapCreate, trailCapUpdate, trailDelete, TRAIL_MODULES
} from '../../utils/activityTrail';
const { TextArea } = Input;
const { Option } = Select;

const ROOT_CAUSES = [
    'Policy gap',
    'Staff knowledge / training',
    'Infrastructure / equipment',
    'Resource / supply shortage',
    'Process / workflow issue',
    'Supervision gap',
    'Documentation failure',
    'Other',
];

const STATUS_CFG = {
    open:        { color: '#1890ff', label: 'Open'        },
    in_progress: { color: '#fa8c16', label: 'In Progress' },
    overdue:     { color: '#f5222d', label: 'Overdue'     },
    closed:      { color: '#52c41a', label: 'Closed'      },
    cancelled:   { color: '#999',    label: 'Cancelled'   },
};

const FULFILLMENT_OPTS = ['Not Met', 'Partially Met'];

export default function QoCCAP() {
    const { periods, currentPeriod } = usePeriods();
    const [caps, setCaps]           = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [clauses, setClauses]     = useState([]);
    const [loading, setLoading]     = useState(false);
    const [modal, setModal]         = useState(false);
    const [form, setForm]           = useState({});
    const [saving, setSaving]       = useState(false);
    const [filters, setFilters]     = useState({ period_label: '', facility_id: '', status: '' });

    useEffect(() => {
        FacilityService.all({ status: 1 }).then(r =>
            setFacilities(r.data?.result?.data || []));
        QoCService.getClauses().then(r =>
            setClauses(r.data?.data || []));
    }, []);

    useEffect(() => {
        if (currentPeriod && !filters.period_label)
            setFilters(f => ({ ...f, period_label: currentPeriod.label }));
    }, [currentPeriod]); // eslint-disable-line

    // eslint-disable-next-line
    useEffect(() => { load(); }, [filters]);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.period_label) params.period_label = filters.period_label;
            if (filters.facility_id)  params.facility_id  = filters.facility_id;
            if (filters.status)       params.status       = filters.status;
            const res = await QoCService.getCapItems(params);
            setCaps(res.data?.data || []);
        } catch { message.error('Failed to load CAP items'); }
        finally { setLoading(false); }
    };

    const openAdd = () => {
        setForm({
            status:       'open',
            period_label: currentPeriod?.label || '',
            clause_ids:   [],    // multi-select clause checkboxes
        });
        setModal(true);
    };

    const openEdit = (row) => {
        setForm({
            ...row,
            clause_ids: row.clause_id ? [String(row.clause_id)] : [],
            target_date: row.target_date ? moment(row.target_date) : null,
        });
        setModal(true);
    };

    const handleSave = async () => {
        if (!form.facility_id) { message.error('Facility is required'); return; }
        if (!form.finding)     { message.error('Finding is required'); return; }
        setSaving(true);
        try {
            // Send first selected clause as clause_id (can be null)
            const payload = {
                ...form,
                clause_id:   form.clause_ids?.[0] || null,
                clause_ids:  undefined,  // not a DB column
                target_date: form.target_date
                    ? moment(form.target_date).format('YYYY-MM-DD')
                    : null,
            };
// REPLACE WITH:
            const res = await QoCService.saveCapItem(payload);
            const facilityName = facilities.find(f => f.id === form.facility_id)?.business_name || String(form.facility_id);
            const clauseRef = form.clause_ids?.[0] ? clauses.find(c => String(c.id) === String(form.clause_ids[0]))?.code || '' : '';
            form.id
                ? trailCapUpdate(form.id, 'CAP item updated', form.status, payload.status)
                : trailCapCreate(facilityName, res?.data?.data?.id || '', clauseRef);
            message.success('CAP item saved');
            setModal(false);
            load();
        } catch { message.error('Save failed'); }
        finally { setSaving(false); }
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: 'Delete this CAP item?',
            okType: 'danger',
            onOk: async () => {
                await QoCService.deleteCapItem(id);
                message.success('Deleted');
                load();
            },
        });
    };

    const handleVerify = (id) => {
        Modal.confirm({
            title: 'Mark this CAP item as closed/verified?',
            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
             onOk: async () => {
                await QoCService.deleteCapItem(id);
                const cap = caps.find(c => c.id === id);
                trailDelete(TRAIL_MODULES.CAP, 'CAP #' + id, id, { facilityName: cap?.facility_name, finding: (cap?.finding || '').slice(0, 60) });
                message.success('Deleted');
                load();
            },
        });
    };

    // Summary stats
    const stats = {
        open:        caps.filter(c => c.status === 'open').length,
        in_progress: caps.filter(c => c.status === 'in_progress').length,
        overdue:     caps.filter(c => c.status === 'overdue').length,
        closed:      caps.filter(c => c.status === 'closed').length,
        cancelled:   caps.filter(c => c.status === 'cancelled').length,
    };
    const total       = caps.length;
    const closureRate = total > 0 ? Math.round((stats.closed / total) * 100) : 0;

    const columns = [
        {
            title: 'Facility', dataIndex: 'facility_name', width: 160, ellipsis: true,
            render: (n, r) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{n}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>{r.site_type}</div>
                </div>
            ),
        },
        {
            title: 'Clause', dataIndex: 'clause_code', width: 75,
            render: (c) => c
                ? <Tag style={{ fontSize: 10, background: '#1890ff', color: '#fff', border: 'none' }}>{c}</Tag>
                : <span style={{ color: '#ccc' }}>—</span>,
        },
        {
            title: 'Finding', dataIndex: 'finding', ellipsis: true,
            render: (v, r) => (
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{v}</div>
                    {r.fulfillment && (
                        <Tag color={r.fulfillment === 'Not Met' ? 'red' : 'orange'}
                            style={{ fontSize: 9, marginTop: 2 }}>
                            {r.fulfillment}
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Root Cause', dataIndex: 'root_cause', width: 130, ellipsis: true,
            render: v => v ? <span style={{ fontSize: 11, color: '#555' }}>{v}</span> : '—',
        },
        {
            title: 'Action', dataIndex: 'action_description', width: 160, ellipsis: true,
            render: v => v ? <span style={{ fontSize: 11 }}>{v}</span> : '—',
        },
        {
            title: 'Responsible', dataIndex: 'responsible_person', width: 110, ellipsis: true,
            render: v => v || '—',
        },
        {
            title: 'Target Date', dataIndex: 'target_date', width: 100,
            render: (d, r) => {
                if (!d) return '—';
                const isOverdue = r.status !== 'closed' && moment(d).isBefore(moment(), 'day');
                return (
                    <span style={{ color: isOverdue ? '#f5222d' : '#333', fontWeight: isOverdue ? 700 : 400, fontSize: 12 }}>
                        {moment(d).format('DD MMM YYYY')}
                        {isOverdue && <div style={{ fontSize: 9, color: '#f5222d' }}>Overdue</div>}
                    </span>
                );
            },
        },
        {
            title: 'Status', dataIndex: 'status', width: 100,
            render: s => {
                const cfg = STATUS_CFG[s] || STATUS_CFG.open;
                return <Tag color={cfg.color} style={{ fontSize: 10 }}>{cfg.label}</Tag>;
            },
        },
        {
            title: '', width: 110, fixed: 'right',
            render: (_, r) => (
                <div style={{ display: 'flex', gap: 4 }}>
                    {r.status !== 'closed' && r.status !== 'cancelled' && (
                        <Tooltip title="Mark as verified & closed">
                            <Button size="small" icon={<CheckCircleOutlined/>}
                                style={{ color: '#52c41a', borderColor: '#b7eb8f' }}
                                onClick={() => handleVerify(r.id)} />
                        </Tooltip>
                    )}
                    <Button size="small" icon={<EditOutlined/>} onClick={() => openEdit(r)} />
                    <Button size="small" icon={<DeleteOutlined/>} danger onClick={() => handleDelete(r.id)} />
                </div>
            ),
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Corrective Action Plans</h2>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        Auto-generated from approved assessments — track remediation per nonconformity
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display: 'flex', gap: 8 }}>
                    <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>Refresh</Button>
                    <Button type="primary" icon={<PlusOutlined/>} onClick={openAdd}>+ Add CAP Item</Button>
                </div>
            </div>

            <div className="page-pad">
                {/* Summary stats */}
                <Row gutter={12} style={{ marginBottom: 16 }}>
                    {[
                        { label: 'Open',        val: stats.open,        color: '#1890ff' },
                        { label: 'In Progress', val: stats.in_progress, color: '#fa8c16' },
                        { label: 'Overdue',     val: stats.overdue,     color: '#f5222d' },
                        { label: 'Closed',      val: stats.closed,      color: '#52c41a' },
                        { label: 'Cancelled',   val: stats.cancelled,   color: '#999'    },
                        { label: 'Closure Rate', val: `${closureRate}%`, color: closureRate >= 70 ? '#52c41a' : '#fa8c16' },
                    ].map(s => (
                        <Col key={s.label} span={4}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                                <Statistic title={s.label} value={s.val}
                                    valueStyle={{ color: s.color, fontSize: 22, fontWeight: 800 }} />
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Filters */}
                <Card size="small" style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: '#888' }}>▽</span>
                        <Select allowClear placeholder="★ All Periods" style={{ width: 160 }}
                            value={filters.period_label || undefined}
                            onChange={v => setFilters(f => ({ ...f, period_label: v || '' }))}>
                            {periods.map(p => (
                                <Option key={p.label} value={p.label}>
                                    {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                </Option>
                            ))}
                        </Select>
                        <Select allowClear placeholder="All Facilities" style={{ width: 200 }}
                            showSearch optionFilterProp="children"
                            value={filters.facility_id || undefined}
                            onChange={v => setFilters(f => ({ ...f, facility_id: v || '' }))}>
                            {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                        </Select>
                        <Select allowClear placeholder="All Statuses" style={{ width: 140 }}
                            value={filters.status || undefined}
                            onChange={v => setFilters(f => ({ ...f, status: v || '' }))}>
                            {Object.entries(STATUS_CFG).map(([k, v]) => (
                                <Option key={k} value={k}>{v.label}</Option>
                            ))}
                        </Select>
                        <Button onClick={() => setFilters({ period_label: '', facility_id: '', status: '' })}>
                            Clear
                        </Button>
                    </div>
                </Card>

                <Card size="small">
                    <Table columns={columns} dataSource={caps} rowKey="id"
                        loading={loading} scroll={{ x: 1200 }}
                        pagination={{ pageSize: 20, showTotal: t => `${t} items` }} />
                </Card>
            </div>

            {/* Add / Edit Modal */}
            <Modal
                open={modal}
                onCancel={() => setModal(false)}
                onOk={handleSave}
                okText="Save"
                confirmLoading={saving}
                title={form.id ? 'Edit CAP Item' : 'Add CAP Item'}
                width={680}
            >
                <Row gutter={[12, 14]}>

                    {/* Facility */}
                    <Col span={12}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Facility *</label>
                        <Select showSearch optionFilterProp="children" style={{ width: '100%' }}
                            value={form.facility_id || undefined}
                            onChange={v => setForm(f => ({ ...f, facility_id: v }))}>
                            {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                        </Select>
                    </Col>

                    {/* Period */}
                    <Col span={12}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Period</label>
                        <Select style={{ width: '100%' }} value={form.period_label || undefined}
                            onChange={v => setForm(f => ({ ...f, period_label: v }))}>
                            {periods.map(p => <Option key={p.label} value={p.label}>{p.label}</Option>)}
                        </Select>
                    </Col>

                    {/* Clauses — checkbox grid */}
                    <Col span={24}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 6 }}>
                            Clause(s) &nbsp;
                            <span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>
                                — select the clause(s) this CAP addresses
                            </span>
                        </label>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                            gap: 6,
                            padding: '10px 12px',
                            background: '#f8faff',
                            borderRadius: 8,
                            border: '1px solid #e0eaff',
                            maxHeight: 160,
                            overflowY: 'auto',
                        }}>
                            {clauses.map(c => {
                                const selected = (form.clause_ids || []).includes(String(c.id));
                                return (
                                    <div
                                        key={c.id}
                                        onClick={() => {
                                            const ids = form.clause_ids || [];
                                            const sid = String(c.id);
                                            setForm(f => ({
                                                ...f,
                                                clause_id: selected ? null : c.id,
                                                ref_no: selected ? '' : c.code,
                                                clause_ids: selected
                                                    ? ids.filter(x => x !== sid)
                                                    : [...ids, sid],
                                            }));
                                        }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 7,
                                            padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
                                            background: selected ? '#1890ff' : '#fff',
                                            border: `1px solid ${selected ? '#1890ff' : '#d9d9d9'}`,
                                            transition: 'all 0.15s',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <span style={{
                                            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                                            background: selected ? '#fff' : '#d9d9d9',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 10, color: selected ? '#1890ff' : 'transparent',
                                        }}>✓</span>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: 11, color: selected ? '#fff' : '#1a1a2e' }}>
                                                {c.code}
                                            </div>
                                            <div style={{ fontSize: 9, color: selected ? 'rgba(255,255,255,0.75)' : '#888', lineHeight: 1.2 }}>
                                                {(c.title || '').slice(0, 22)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {(form.clause_ids || []).length > 0 && (
                            <div style={{ marginTop: 6, fontSize: 11, color: '#1890ff' }}>
                                Selected: {(form.clause_ids || []).map(id => {
                                    const c = clauses.find(x => String(x.id) === id);
                                    return c?.code;
                                }).filter(Boolean).join(', ')}
                            </div>
                        )}
                    </Col>

                    {/* Finding */}
                    <Col span={24}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Finding *</label>
                        <TextArea rows={3} value={form.finding || ''}
                            onChange={e => setForm(f => ({ ...f, finding: e.target.value }))}
                            placeholder="Describe the nonconformity observed..." />
                    </Col>

                    {/* Fulfillment + Root Cause */}
                    <Col span={12}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Fulfillment</label>
                        <Select allowClear style={{ width: '100%' }} value={form.fulfillment || undefined}
                            onChange={v => setForm(f => ({ ...f, fulfillment: v }))}>
                            {FULFILLMENT_OPTS.map(o => <Option key={o} value={o}>{o}</Option>)}
                        </Select>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Root Cause</label>
                        <Select allowClear style={{ width: '100%' }} value={form.root_cause || undefined}
                            onChange={v => setForm(f => ({ ...f, root_cause: v }))}>
                            {ROOT_CAUSES.map(r => <Option key={r} value={r}>{r}</Option>)}
                        </Select>
                    </Col>

                    {/* Corrective Action */}
                    <Col span={24}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Corrective Action</label>
                        <TextArea rows={3} value={form.action_description || ''}
                            onChange={e => setForm(f => ({ ...f, action_description: e.target.value }))}
                            placeholder="Describe the remediation plan..." />
                    </Col>

                    {/* Responsible + Target Date + Status */}
                    <Col span={8}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Responsible Person</label>
                        <Input value={form.responsible_person || ''}
                            onChange={e => setForm(f => ({ ...f, responsible_person: e.target.value }))}
                            placeholder="Name and role" />
                    </Col>
                    <Col span={8}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Target Date</label>
                        <DatePicker style={{ width: '100%' }}
                            value={form.target_date ? moment(form.target_date) : null}
                            onChange={v => setForm(f => ({ ...f, target_date: v }))} />
                    </Col>
                    <Col span={8}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Status</label>
                        <Select style={{ width: '100%' }} value={form.status || 'open'}
                            onChange={v => setForm(f => ({ ...f, status: v }))}>
                            {Object.entries(STATUS_CFG).map(([k, v]) => (
                                <Option key={k} value={k}>{v.label}</Option>
                            ))}
                        </Select>
                    </Col>

                </Row>
            </Modal>
        </div>
    );
}